import React from "react";
import { Check } from "lucide-react";
import { SCROLL_OFFSET } from "./sections";

const COLUMNS = {
  google: "Google's Built-In Voice Typing",
  zeroTyping: "Zero Typing AI",
};

const ROWS = [
  {
    feature: "Punctuation",
    google: "Requires spoken commands",
    zeroTyping: "Added automatically",
  },
  {
    feature: "Accuracy on technical terms",
    google: "Inconsistent",
    zeroTyping: "Higher accuracy",
  },
  {
    feature: "Works across other apps",
    google: "No, Docs only",
    zeroTyping: "Yes, Gmail, Slack, AI chat tools",
  },
  {
    feature: "Formatting",
    google: "Manual clean-up needed",
    zeroTyping: "Clean text as you speak",
  },
  {
    feature: "Built for",
    google: "Short notes",
    zeroTyping: "Long-form writing",
  },
];

const CARD_SHADOW =
  "0px 10.73px 32.19px -12.88px #173A8C24, 0px 1.07px 2.15px 0px #0A0E190A";

// Shared header type from the spec: Inter 700 / 12.88px / 19.32px / 0.32px, uppercase.
const LABEL =
  "font-inter text-[12.88px] font-bold uppercase leading-[19.32px] tracking-[0.32px]";
const GRADIENT_LABEL =
  "inline-block bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] bg-clip-text text-transparent";

const HEAD_CELL = `align-top px-[16px] lg:px-[21.46px] py-[17.17px] text-left ${LABEL}`;
const BODY_CELL =
  "align-top px-[16px] lg:px-[21.46px] py-[17.17px] font-inter text-[14px] leading-[24px] tracking-[0px]";

const ZeroTypingValue = ({ children }) => (
  <span className="flex items-start gap-2">
    <Check
      className="mt-[5px] h-[14px] w-[14px] shrink-0 text-[#3AB1F6]"
      strokeWidth={2.5}
    />
    <span>{children}</span>
  </span>
);

const ComparisonSection = () => {
  return (
    <section
      id="vs-google"
      style={{ scrollMarginTop: `${SCROLL_OFFSET}px` }}
      className="w-full lg:max-w-[815.58px]"
    >
      <h2 className="font-jakarta font-bold text-[#0A0D16] text-[28px] sm:text-[36px] md:text-[44px] lg:text-[50px] leading-[1.28] lg:leading-[64px] tracking-[-0.8px]">
        Zero Typing AI vs. Google&apos;s Built-In Voice Typing
      </h2>

      <div className="pt-[25.76px]">
        {/*
          Below md the three columns cannot fit without shrinking text past
          legibility, so each row becomes a self-contained card. The table
          renders from md up, where the spec's 600.95px minimum fits.
        */}
        <div className="flex flex-col gap-[12px] md:hidden">
          {ROWS.map(({ feature, google, zeroTyping }) => (
            <article
              key={feature}
              className="overflow-hidden rounded-[17.17px] border-[1.34px] border-[#E6ECF5]"
              style={{ boxShadow: CARD_SHADOW }}
            >
              <h3 className="border-b-[1.07px] border-[#E6ECF5] bg-[#F7FAFF] px-[16px] py-[12px] font-inter text-[15px] font-bold leading-[24px] text-[#0A0D16]">
                {feature}
              </h3>

              <div className="border-b-[1.07px] border-[#E6ECF5] px-[16px] py-[14px]">
                <p className={`${LABEL} text-[#0A0D16B2]`}>{COLUMNS.google}</p>
                <p className="mt-[4px] font-inter text-[14px] font-normal leading-[24px] text-[#0A0D1699]">
                  {google}
                </p>
              </div>

              <div className="bg-[#EBF7FE] px-[16px] py-[14px]">
                <p className={LABEL}>
                  <span className={GRADIENT_LABEL}>{COLUMNS.zeroTyping}</span>
                </p>
                <p className="mt-[4px] font-inter text-[14px] font-normal leading-[24px] text-[#0A0D16]">
                  <ZeroTypingValue>{zeroTyping}</ZeroTypingValue>
                </p>
              </div>
            </article>
          ))}
        </div>

        {/* Table - md and up */}
        <div
          className="hidden overflow-hidden rounded-[17.17px] border-[1.34px] border-[#E6ECF5] md:block"
          style={{ boxShadow: CARD_SHADOW }}
        >
          <table className="w-full table-fixed border-collapse">
            <colgroup>
              <col className="w-[32.1%]" />
              <col className="w-[32.1%]" />
              <col className="w-[35.8%]" />
            </colgroup>

            <thead>
              <tr>
                <th
                  scope="col"
                  className={`${HEAD_CELL} border-b-[1.34px] border-[#E6ECF5] bg-[#F7FAFF] text-[#0A0D16B2]`}
                >
                  Feature
                </th>
                <th
                  scope="col"
                  className={`${HEAD_CELL} border-b-[1.34px] border-[#E6ECF5] bg-[#F7FAFF] text-[#0A0D16B2]`}
                >
                  {COLUMNS.google}
                </th>
                <th
                  scope="col"
                  className={`${HEAD_CELL} border-b-[1.34px] border-[#E6ECF5] bg-[#EBF7FE]`}
                >
                  <span className={GRADIENT_LABEL}>{COLUMNS.zeroTyping}</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {ROWS.map(({ feature, google, zeroTyping }, index) => {
                const divider =
                  index === ROWS.length - 1
                    ? ""
                    : "border-b-[1.07px] border-[#E6ECF5]";

                return (
                  <tr key={feature}>
                    <th
                      scope="row"
                      className={`${BODY_CELL} ${divider} text-left font-bold text-[#0A0D16]`}
                    >
                      {feature}
                    </th>
                    <td
                      className={`${BODY_CELL} ${divider} font-normal text-[#0A0D1699]`}
                    >
                      {google}
                    </td>
                    <td
                      className={`${BODY_CELL} ${divider} bg-[#EBF7FE] font-normal text-[#0A0D16]`}
                    >
                      <ZeroTypingValue>{zeroTyping}</ZeroTypingValue>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;
